/**
 * PromptSuggestionButton — universal "✨ AI Suggest" chip.
 *
 * Drops next to any open-ended form field. Gives station owners:
 *   • 5 LLM-generated, station-aware, broadcast-ready ideas tuned to
 *     the active workspace (genre, city, tagline) + context fields
 *   • A star icon on each suggestion to save it to a personal Prompt
 *     Library (`prompt_favorites` collection on the backend)
 *   • A "★ Saved" row at the top of the popover that surfaces the user's
 *     favorites for this (surface, field) so they can re-use top performers
 *     without re-generating
 *   • Re-use of a saved chip bumps the usage counter, powering the
 *     Dashboard "Most-reused prompts" widget
 *
 * Used by AI DJ Assistant, News Center, Commercials, Imaging Studio.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Wand2, Loader2, Sparkles, X, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function PromptSuggestionButton({
  surface,
  subtype,
  fieldName,
  context,
  onSelect,
  testid,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [saved, setSaved] = useState([]);
  // Map of text → fav_id for quick "is-saved" checks in the suggestions row
  const [savedByText, setSavedByText] = useState({});

  const fetchSaved = useCallback(async () => {
    try {
      const { data } = await api.get("/prompts/favorites", {
        params: { surface, field: fieldName, limit: 30 },
      });
      const list = data?.items || [];
      setSaved(list);
      const m = {};
      for (const f of list) m[f.text] = f.fav_id;
      setSavedByText(m);
    } catch { /* unauthenticated or empty */ }
  }, [surface, fieldName]);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      // Fire saved + suggestions in parallel
      const [_s, sug] = await Promise.all([
        fetchSaved(),
        api.post("/prompts/suggest", {
          surface,
          subtype: subtype || null,
          field: fieldName,
          context: context || {},
          count: 5,
        }),
      ]);
      setItems(sug.data?.suggestions || []);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch ideas");
    } finally {
      setLoading(false);
    }
  }, [fetchSaved, surface, subtype, fieldName, context]);

  // Refresh just the saved list when the popover re-opens
  useEffect(() => { if (open) fetchSaved(); }, [open, fetchSaved]);

  const handleSelect = (text, favId) => {
    onSelect(text);
    setOpen(false);
    if (favId) {
      // Bump usage counter — fire & forget
      api.post(`/prompts/favorites/${favId}/used`).catch(() => {});
    }
  };

  const handleSaveToggle = async (e, text) => {
    e.stopPropagation();
    e.preventDefault();
    const existingFavId = savedByText[text];
    if (existingFavId) {
      try {
        await api.delete(`/prompts/favorites/${existingFavId}`);
        setSavedByText((m) => {
          const n = { ...m };
          delete n[text];
          return n;
        });
        setSaved((cur) => cur.filter((f) => f.fav_id !== existingFavId));
        toast.success("Removed from library");
      } catch { toast.error("Couldn't remove"); }
    } else {
      try {
        const { data } = await api.post("/prompts/favorites", {
          surface, subtype: subtype || null, field: fieldName, text,
        });
        const fav = data?.favorite;
        if (fav?.fav_id) {
          setSavedByText((m) => ({ ...m, [text]: fav.fav_id }));
          setSaved((cur) => [fav, ...cur.filter((f) => f.fav_id !== fav.fav_id)]);
          toast.success("Saved to library");
        }
      } catch { toast.error("Couldn't save"); }
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={open ? () => setOpen(false) : fetchSuggestions}
        disabled={loading}
        data-testid={testid}
        className={`inline-flex items-center gap-1 rounded-md border border-violet-500/40 bg-violet-500/10 ${compact ? "px-1.5 py-0.5" : "px-2 py-1"} text-[10px] font-medium uppercase tracking-wider text-violet-300 hover:bg-violet-500/20 transition disabled:opacity-50`}
        title="Generate broadcast-ready ideas for this field"
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
        {!compact && "AI Suggest"}
      </button>
      {open && (
        <div
          className="absolute right-0 z-30 mt-1.5 w-[320px] sm:w-[440px] rounded-lg border border-line bg-bg-surface shadow-xl p-2.5 animate-in fade-in slide-in-from-top-1"
          data-testid={`${testid}-popover`}
        >
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-violet-300 inline-flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Pick · star to save · refresh for fresh
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={fetchSuggestions}
                disabled={loading}
                data-testid={`${testid}-refresh`}
                className="h-5 w-5 rounded-md text-ink-muted hover:text-violet-300 hover:bg-bg-elevated flex items-center justify-center disabled:opacity-50"
                title="Generate new suggestions"
              >
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid={`${testid}-close`}
                className="h-5 w-5 rounded-md text-ink-muted hover:text-rose-300 hover:bg-bg-elevated flex items-center justify-center"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>

          {/* ★ Saved row — shows the user's favorites for this field */}
          {saved.length > 0 && (
            <div className="mb-2" data-testid={`${testid}-saved`}>
              <div className="flex items-center gap-1.5 px-1 mb-1 text-[9px] font-mono uppercase tracking-wider text-amber-400">
                <Star className="h-3 w-3 fill-amber-400" />
                Saved
                <span className="text-ink-muted normal-case">({saved.length})</span>
              </div>
              <ul className="space-y-1 max-h-[120px] overflow-y-auto">
                {saved.map((f) => (
                  <li key={f.fav_id} className="group flex items-stretch gap-1">
                    <button
                      type="button"
                      onClick={() => handleSelect(f.text, f.fav_id)}
                      data-testid={`${testid}-saved-${f.fav_id}`}
                      className="flex-1 text-left rounded-md border border-amber-400/30 bg-amber-400/5 px-2.5 py-1.5 text-xs text-ink-primary hover:bg-amber-400/15 hover:border-amber-400/60 hover:text-amber-100 transition leading-relaxed"
                    >
                      <span className="line-clamp-2">{f.text}</span>
                      {f.used_count > 0 && (
                        <span className="ml-1 text-[9px] font-mono text-amber-300/70">· used {f.used_count}×</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleSaveToggle(e, f.text)}
                      data-testid={`${testid}-saved-remove-${f.fav_id}`}
                      title="Remove from library"
                      className="opacity-0 group-hover:opacity-100 transition w-7 shrink-0 rounded-md border border-line hover:border-rose-500/40 hover:bg-rose-500/10 text-ink-muted hover:text-rose-300 flex items-center justify-center"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
              {items.length > 0 && (
                <div className="mt-2 mb-1 flex items-center gap-2 px-1">
                  <div className="h-px flex-1 bg-line" />
                  <div className="text-[9px] font-mono uppercase tracking-wider text-ink-muted">Fresh ideas</div>
                  <div className="h-px flex-1 bg-line" />
                </div>
              )}
            </div>
          )}

          {/* Fresh AI suggestions row */}
          {items.length === 0 ? (
            <div className="text-xs text-ink-muted px-1 py-3 text-center">
              {saved.length ? "Tap refresh for fresh ideas." : "No ideas yet — try refreshing."}
            </div>
          ) : (
            <ul className="space-y-1 max-h-[220px] overflow-y-auto">
              {items.map((s, i) => {
                const isSaved = !!savedByText[s];
                return (
                  <li key={i} className="group flex items-stretch gap-1">
                    <button
                      type="button"
                      onClick={() => handleSelect(s, savedByText[s])}
                      data-testid={`${testid}-item-${i}`}
                      className="flex-1 text-left rounded-md border border-line bg-bg-elevated/40 px-2.5 py-2 text-xs text-ink-primary hover:bg-violet-500/10 hover:border-violet-500/40 hover:text-violet-200 transition leading-relaxed"
                    >
                      {s}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleSaveToggle(e, s)}
                      data-testid={`${testid}-star-${i}`}
                      title={isSaved ? "Remove from library" : "Save to library"}
                      className={`w-7 shrink-0 rounded-md border flex items-center justify-center transition ${
                        isSaved
                          ? "border-amber-400/60 bg-amber-400/15 text-amber-300"
                          : "border-line text-ink-muted hover:border-amber-400/40 hover:text-amber-300 hover:bg-amber-400/10"
                      }`}
                    >
                      <Star className={`h-3 w-3 ${isSaved ? "fill-amber-300" : ""}`} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
